/**
 * `localStorage`, typed and survivable.
 *
 * Two problems, and the second is the one that matters. The first is spelling:
 * `localStorage.getItem("khazzin-theme")` next to a write of `"khazzin:theme"`
 * is a bug nothing catches, because both halves succeed and the value simply
 * never comes back. The keys below are the whole vocabulary, and a key outside
 * them does not type-check.
 *
 * The second is that **every access can throw**. Safari in private browsing
 * throws on `setItem` once the quota is reached — which is zero — and a browser
 * with site data blocked throws on the `localStorage` property access itself,
 * before any method is called. A `try` around the write is not enough: reading
 * the global is the part that fails. So every function here catches, and the
 * caller gets a fallback rather than a stack trace. Persisting the sidebar's
 * collapsed state must never be able to prevent a screen from rendering.
 *
 * Nothing here is state the server needs. These are per-browser conveniences —
 * which theme to paint before `/me/context` answers, which columns this person
 * hid on this screen. Anything that has to follow somebody to another device is
 * a preference, and preferences live in the API (EP00-S03).
 */

/**
 * The keys, with the `khazzin:` prefix that keeps them out of the way of
 * anything else sharing the origin.
 *
 * `columns:<screenKey>` is the one parameterised key; {@link columnsKey} builds
 * it so the prefix is written once.
 */
export const KEYS = {
	/** The chosen theme, read before the session resolves so the first paint is right. */
	theme: "khazzin:theme",
	/** The chosen locale, for the same reason: `dir` must be set before anything renders. */
	locale: "khazzin:locale",
	/** Whether the sidebar is collapsed. */
	sidebar: "khazzin:sidebar",
	/** The last evaluated flag set, so a cold start does not flash un-gated UI. */
	flags: "khazzin:flags",
	/** The command palette's recent choices, most recent first. */
	paletteRecent: "khazzin:palette:recent",
	/**
	 * The warehouse the console is looking at, stamped with whose choice it is.
	 *
	 * Per browser rather than a preference that follows somebody between
	 * devices, because `10/00` §3 closes `preference` at four settings and says
	 * plainly that anything else belongs to the screen that cares. The user id
	 * inside the value is what keeps a shared warehouse terminal from handing
	 * the next person the site the last one was standing in.
	 */
	warehouse: "khazzin:warehouse",
} as const;

/** One of the fixed keys. */
export type StorageKey = (typeof KEYS)[keyof typeof KEYS];

/** The per-screen column visibility key. */
export function columnsKey(screenKey: string): string {
	return `khazzin:columns:${screenKey}`;
}

/**
 * Reads a string, or `null` when it is absent or storage is unavailable.
 *
 * The two are deliberately the same answer. A caller that behaved differently
 * when storage was blocked than when the key was simply unset would be
 * branching on the browser's privacy settings, which is not a distinction any
 * screen in this product should make.
 */
export function readString(key: string): string | null {
	try {
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
}

/** Writes a string. Silently does nothing when storage is unavailable. */
export function writeString(key: string, value: string): void {
	try {
		window.localStorage.setItem(key, value);
	} catch {
		// Private browsing, a full quota, or site data blocked. None of them are
		// the user's problem and none of them should surface as one.
	}
}

/** Removes a key. */
export function remove(key: string): void {
	try {
		window.localStorage.removeItem(key);
	} catch {
		// As above.
	}
}

/**
 * Reads a string constrained to a known set.
 *
 * Anything outside the set is treated as absent, which is what makes a stored
 * value survive the set shrinking: a browser still holding `theme=solarized`
 * from a build that offered it gets the fallback rather than an attribute no
 * stylesheet answers to.
 */
export function readOneOf<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
	const raw = readString(key);
	return allowed.includes(raw as T) ? (raw as T) : fallback;
}

/**
 * Reads JSON, or the fallback.
 *
 * The fallback covers three separate failures — storage unavailable, key
 * absent, value unparseable — and returns the same thing for all three on
 * purpose. Stored JSON is a cache of something the server owns; when it cannot
 * be trusted the answer is to go and ask, not to reason about why.
 */
export function readJSON<T>(key: string, fallback: T): T {
	const raw = readString(key);
	if (raw === null) {
		return fallback;
	}
	try {
		return JSON.parse(raw) as T;
	} catch {
		// A half-written value from a tab that was closed mid-write, or a shape
		// from an older build. Either way it is not worth keeping.
		remove(key);
		return fallback;
	}
}

/** Writes JSON. A value that cannot be serialised is dropped rather than thrown. */
export function writeJSON(key: string, value: unknown): void {
	try {
		writeString(key, JSON.stringify(value));
	} catch {
		// A circular structure. A programming error, but not one worth taking a
		// screen down for — the value simply is not cached.
	}
}
